// ABOUTME: Full-screen modal editors for system prompt and tool prompts.
// ABOUTME: Provides a clean, readable editing experience with large text areas.

import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Code } from 'lucide-react';
import { Button } from '@/components/ui/Button';

// Variable definitions for system prompt
const PROMPT_VARIABLES = [
  { name: '{{workspace_name}}', description: 'Name of the current workspace' },
  { name: '{{user_name}}', description: 'Name of the current user' },
  { name: '{{data_source_type}}', description: 'Type of connected data source' },
  { name: '{{current_date}}', description: 'Current date (YYYY-MM-DD)' },
];

// System Prompt Editor Modal
interface SystemPromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  defaultValue: string;
}

export function SystemPromptEditorModal({
  isOpen,
  onClose,
  value,
  onChange,
  defaultValue,
}: SystemPromptEditorModalProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isUsingDefault = !value || value === defaultValue;
  const displayContent = value || defaultValue;

  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = displayContent.slice(0, start) + variable + displayContent.slice(end);
      onChange(newValue);
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      onChange(displayContent + variable);
    }
  };

  const handleReset = () => {
    // Set to empty string to indicate "use default"
    onChange('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full h-full max-w-6xl max-h-[95vh] m-4 bg-surface-0 rounded-sm border border-surface-200 shadow-modal flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 bg-surface-50">
            <div>
              <h2 className="text-xl font-semibold text-surface-800">Dataset-Specific Instructions</h2>
              <p className="text-sm text-surface-500 mt-0.5">
                {isUsingDefault ? 'No custom instructions' : 'Custom instructions'} • {displayContent.length.toLocaleString()} characters
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isUsingDefault && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                  onClick={handleReset}
                >
                  Reset to Default
                </Button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-sm hover:bg-surface-100 text-surface-500 hover:text-surface-800 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Variable Toolbar */}
          <div className="px-6 py-3 border-b border-surface-200 bg-surface-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-surface-500 mr-2">Insert Variable:</span>
              {PROMPT_VARIABLES.map((variable) => (
                <button
                  key={variable.name}
                  onClick={() => insertVariable(variable.name)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors"
                  title={variable.description}
                >
                  <Code className="w-3 h-3" />
                  {variable.name}
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1 overflow-hidden p-6">
            <textarea
              ref={textareaRef}
              value={displayContent}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm font-mono leading-relaxed resize-none focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
              placeholder="Enter your system prompt..."
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-surface-200 bg-surface-50">
            <p className="text-xs text-surface-500">
              Add dataset-specific context: table structures, query patterns, business logic, and domain knowledge. This is appended to the default system prompt.
            </p>
            <Button onClick={onClose}>Done</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Tool Prompt Editor Modal
import type { ToolDefinition } from '@/types/admin';
import { Lock } from 'lucide-react';

interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  value: string;
  onChange: (value: string) => void;
  defaultValue: string;
  toolDefinition?: ToolDefinition | null;
}

export function PromptEditorModal({
  isOpen,
  onClose,
  title,
  value,
  onChange,
  defaultValue,
  toolDefinition,
}: PromptEditorModalProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isUsingDefault = !value || value === defaultValue;
  const displayContent = value || defaultValue;

  const handleReset = () => {
    // Set to empty string to indicate "use default"
    onChange('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-4xl max-h-[90vh] m-4 bg-surface-0 rounded-sm border border-surface-200 shadow-modal flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
            <h2 className="text-lg font-semibold text-surface-800">{title}</h2>
            <div className="flex items-center gap-2">
              {!isUsingDefault && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<RotateCcw className="w-4 h-4" />}
                  onClick={handleReset}
                >
                  Reset to Default
                </Button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-sm hover:bg-surface-100 text-surface-500 hover:text-surface-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Content - JSON-like structure */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-1 font-mono text-sm">
              {/* Opening brace */}
              <div className="text-surface-500">{'{'}</div>

              {/* name field - locked */}
              <div className="flex items-start gap-2 pl-6">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-primary-600">"name"</span>
                  <span className="text-surface-500">:</span>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-success">"{toolDefinition?.name || 'unknown'}"</span>
                  <Lock className="w-3 h-3 text-surface-400" />
                </div>
              </div>

              {/* description field - editable */}
              <div className="pl-6 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-primary-600">"description"</span>
                  <span className="text-surface-500">:</span>
                  {isUsingDefault && (
                    <span className="text-[10px] px-2 py-0.5 rounded-sm bg-success-light text-success font-medium font-sans">
                      Default
                    </span>
                  )}
                </div>
                <textarea
                  ref={textareaRef}
                  value={displayContent}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-full min-h-[400px] px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-success text-sm font-mono leading-relaxed resize-y focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
                  placeholder="Enter tool description..."
                />
                <p className="text-xs text-surface-500 mt-1 font-sans">
                  {displayContent.length.toLocaleString()} characters
                </p>
              </div>

              {/* input_schema field - locked, collapsible */}
              <div className="pl-6 pt-4">
                <details className="group">
                  <summary className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-surface-500 group-open:rotate-90 transition-transform">▶</span>
                    <span className="text-primary-600">"input_schema"</span>
                    <span className="text-surface-500">:</span>
                    <span className="text-surface-500 group-open:hidden">{'{ ... }'}</span>
                    <Lock className="w-3 h-3 text-surface-400" />
                  </summary>
                  <div className="mt-2 ml-4 p-4 rounded-sm bg-surface-100 border border-surface-200 overflow-x-auto">
                    <pre className="text-xs text-surface-600 whitespace-pre">
                      {JSON.stringify(toolDefinition?.input_schema || {}, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>

              {/* Closing brace */}
              <div className="text-surface-500 pt-2">{'}'}</div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-surface-200">
            <Button onClick={onClose}>Done</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PromptEditorModal;
